import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Upload, Image as ImageIcon, Crop, X, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import Cropper from "react-easy-crop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function api(token) {
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

function createCroppedBlob(imageSrc, cropPixels) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = cropPixels.width;
      canvas.height = cropPixels.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, cropPixels.width, cropPixels.height);
      canvas.toBlob((blob) => blob ? resolve(blob) : reject("Crop failed"), "image/jpeg", 0.92);
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}

export default function AdminTransformationsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await api(token).get("/transformations");
      setItems(data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this transformation?")) return;
    try {
      await api(token).delete(`/transformations/${id}`);
      toast.success("Deleted");
      fetchItems();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-transformations">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide flex items-center gap-2"><ImageIcon size={18} className="text-gg-red" /> Transformations ({items.length})</h2>
        <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="add-transformation-btn">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      <AnimatePresence>
        {showAdd && <AddForm token={token} onDone={() => { setShowAdd(false); fetchItems(); }} onCancel={() => setShowAdd(false)} />}
      </AnimatePresence>

      {items.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-8">No transformations yet. Upload before/after images of your clients.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-gg-surface rounded-xl border border-white/5 overflow-hidden">
              <div className="grid grid-cols-2">
                <div className="aspect-[4/5] overflow-hidden"><img src={item.before_image} alt="Before" className="w-full h-full object-cover" /></div>
                <div className="aspect-[4/5] overflow-hidden"><img src={item.after_image} alt="After" className="w-full h-full object-cover" /></div>
              </div>
              <div className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">{item.client_name}</p>
                  {item.duration && <span className="text-xs text-gg-red">{item.duration}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-400" data-testid={`delete-transformation-${item.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== IMAGE UPLOAD WITH CROP ====================
function ImageUploadWithCrop({ token, label, value, onChange, aspect }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);

  const onCropComplete = useCallback((_, croppedPixels) => { setCroppedArea(croppedPixels); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropSave = async () => {
    if (!croppedArea || !cropSrc) return;
    setUploading(true);
    try {
      const blob = await createCroppedBlob(cropSrc, croppedArea);
      const formData = new FormData();
      formData.append("file", new File([blob], "cropped.jpg", { type: "image/jpeg" }));
      const { data } = await api(token).post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${API}/files/${data.path}`);
      setCropSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      toast.success("Image cropped & uploaded!");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleUploadOriginal = async () => {
    if (!cropSrc) return;
    setUploading(true);
    try {
      const res = await fetch(cropSrc);
      const blob = await res.blob();
      const formData = new FormData();
      formData.append("file", new File([blob], "original.jpg", { type: "image/jpeg" }));
      const { data } = await api(token).post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${API}/files/${data.path}`);
      setCropSrc(null);
      toast.success("Image uploaded!");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div>
      <p className="text-[10px] uppercase text-zinc-500 mb-1.5 flex items-center gap-1"><Crop size={10} /> {label}</p>

      {/* Crop Modal */}
      <AnimatePresence>
        {cropSrc && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCropSrc(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="bg-gg-surface border border-white/10 rounded-xl w-full max-w-md overflow-hidden">
              <div className="p-3 border-b border-white/5 flex items-center justify-between">
                <span className="font-heading text-xs uppercase tracking-wider text-zinc-400">Crop Image</span>
                <button onClick={() => setCropSrc(null)} className="text-zinc-500 hover:text-white"><X size={16} /></button>
              </div>
              <div className="relative h-72 bg-gg-black">
                <Cropper
                  image={cropSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{ containerStyle: { borderRadius: 0 } }}
                />
              </div>
              <div className="p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <ZoomIn size={12} className="text-zinc-500" />
                  <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 accent-[#FF3B30] h-1" />
                  <span className="text-[10px] text-zinc-500 w-8">{zoom.toFixed(1)}x</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCropSave} disabled={uploading} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg flex-1" data-testid="crop-save-btn">
                    <Crop size={12} className="mr-1" /> {uploading ? "..." : "Crop & Upload"}
                  </Button>
                  <Button onClick={handleUploadOriginal} disabled={uploading} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-lg" data-testid="upload-original-btn">
                    {uploading ? "..." : "Use Original"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview or Upload Button */}
      {value ? (
        <div className="relative rounded-lg overflow-hidden aspect-[4/5]">
          <img src={value} alt={label} className="w-full h-full object-cover" />
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            <button type="button" onClick={() => fileRef.current?.click()} className="bg-black/60 backdrop-blur text-white rounded p-1 hover:bg-gg-red/80 transition-colors"><Crop size={12} /></button>
            <button type="button" onClick={() => onChange("")} className="bg-black/60 backdrop-blur text-white rounded p-1 hover:bg-red-500/80 transition-colors"><Trash2 size={12} /></button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full aspect-[4/5] border border-dashed border-white/10 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-gg-red transition-colors">
          <Upload size={20} className="text-zinc-600" />
          <span className="text-[10px] text-zinc-600">{uploading ? "Uploading..." : "Upload Image"}</span>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
    </div>
  );
}

// ==================== ADD FORM ====================
function AddForm({ token, onDone, onCancel }) {
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");
  const [desc, setDesc] = useState("");
  const [beforeImg, setBeforeImg] = useState("");
  const [afterImg, setAfterImg] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!beforeImg || !afterImg) { toast.error("Both before & after images required"); return; }
    setSaving(true);
    try {
      await api(token).post("/transformations", { client_name: name, before_image: beforeImg, after_image: afterImg, description: desc, duration });
      toast.success("Transformation added!");
      onDone();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  return (
    <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit} className="bg-gg-surface rounded-xl p-4 border border-white/5 space-y-3" data-testid="add-transformation-form">
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Member Name" value={name} onChange={e => setName(e.target.value)} required className="bg-gg-black border-white/10 text-white rounded-lg h-10" />
        <Input placeholder="Duration (e.g., 3 Months)" value={duration} onChange={e => setDuration(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-10" />
      </div>
      <Textarea placeholder="Description (optional)" value={desc} onChange={e => setDesc(e.target.value)} rows={2} className="bg-gg-black border-white/10 text-white rounded-lg text-sm" />

      <div className="grid grid-cols-2 gap-3">
        <ImageUploadWithCrop token={token} label="Before Image" value={beforeImg} onChange={setBeforeImg} aspect={4/5} />
        <ImageUploadWithCrop token={token} label="After Image" value={afterImg} onChange={setAfterImg} aspect={4/5} />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="save-transformation-btn">{saving ? "..." : "Save"}</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="text-zinc-400">Cancel</Button>
      </div>
    </motion.form>
  );
}
